import React, { useState } from 'react';
import { Ticket, Info, Building2 } from 'lucide-react';
import { Movie } from '../../types';

interface MovieCardProps {
  movie: Movie;
  onBook: (id: number) => void;
  onViewMore: (id: number) => void;
  forceDark?: boolean;
  eventEnded?: boolean;
}

export const MovieCard: React.FC<MovieCardProps> = ({ movie, onBook, onViewMore, eventEnded }) => {
  const [isHovered, setIsHovered] = useState(false);

  const isBookingClosed = (() => {
    const now = new Date();
    const startOk = movie.booking_start_time ? now >= new Date(movie.booking_start_time) : true;
    const stopOk = movie.booking_stop_enabled ? (!!movie.booking_stop_time && now < new Date(movie.booking_stop_time)) : true;
    return !movie.accept_booking || !startOk || !stopOk;
  })();

  const rootClass = "group relative w-full max-w-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 ease-out overflow-hidden flex flex-col border border-gray-100 dark:border-gray-700";
  const contentClass = "relative z-10 flex-1 px-6 pb-6 pt-2 bg-white dark:bg-gray-800 flex flex-col transition-colors duration-300";
  const priceTextClass = "text-xl font-black text-gray-800 dark:text-white tracking-tight";
  const organizerChipClass = "inline-flex items-center gap-1.5 py-1.5 px-3 rounded-md bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 text-blue-600 dark:text-blue-300 text-[10px] font-bold uppercase tracking-wider shadow-sm";
  const descriptionClass = "text-gray-600 dark:text-gray-300 text-sm leading-relaxed line-clamp-3 mb-6 flex-grow";
  const secondaryButtonClass = "flex-1 flex items-center justify-center gap-2 bg-white dark:bg-transparent text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-neutral-700 py-3 px-4 rounded-lg font-bold text-sm uppercase tracking-wide hover:bg-gray-50 dark:hover:bg-neutral-800 hover:text-spy-dark dark:hover:text-white hover:border-gray-400 dark:hover:border-neutral-500 active:scale-95 transition-all";

  return (
    <div
      className={rootClass}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Section with Angled Clip Path */}
      <div className="relative h-80 w-full overflow-hidden">
        <div
          className="absolute inset-0 bg-gray-100 dark:bg-gray-900"
          style={{
            // Angled cut at the bottom
            clipPath: 'polygon(0 0, 100% 0, 100% 85%, 0 100%)',
            zIndex: 10
          }}
        >
          <img
            src={movie.image_url}
            alt={movie.title}
            className={`w-full h-full object-cover object-top transition-transform duration-700 ease-out ${isHovered ? 'scale-110' : 'scale-100'}`}
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60"></div>

          {/* Badge/Genre Tag overlay */}
          {movie.genre && (
            <span className="absolute top-4 right-4 bg-black/60 backdrop-blur-md text-white text-xs font-bold px-3 py-1 rounded-full border border-white/20 uppercase tracking-wider shadow-sm">
              {movie.genre}
            </span>
          )}
          {movie.is_canceled && (
            <span className="absolute top-4 left-4 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest shadow-sm">
              Cancelled
            </span>
          )}
          {!movie.is_canceled && eventEnded && (
            <span className="absolute top-4 left-4 bg-gray-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest shadow-sm">
              Ended
            </span>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className={contentClass}>
        {/* Header: Title and Price */}
        <div className="flex justify-between items-start mb-3">
          <h2 className="text-2xl font-extrabold text-spy-red dark:text-red-500 uppercase tracking-tight leading-tight flex-1 pr-4">
            {movie.title}
          </h2>
          <div className="flex-shrink-0">
            <span className={priceTextClass}>
              €{movie.price.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Organizer Chip */}
        <div className="mb-4 mt-0">
          <span className={organizerChipClass}>
            <Building2 size={12} className="text-blue-500 dark:text-blue-400" />
            {movie.organizer || "Unknown Organizer"}
          </span>
        </div>

        {/* Description */}
        <p className={descriptionClass}>
          {movie.description}
        </p>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-auto">
          <button
            onClick={() => { if (!movie.is_canceled && !isBookingClosed && !eventEnded) onBook(movie.id) }}
            disabled={!!movie.is_canceled || isBookingClosed || !!eventEnded}
            className={movie.is_canceled || isBookingClosed || eventEnded
              ? "flex-1 flex items-center justify-center gap-2 bg-gray-300 text-gray-700 py-3 px-4 rounded-lg font-bold text-sm uppercase tracking-wide cursor-not-allowed"
              : "flex-1 flex items-center justify-center gap-2 bg-white text-black py-3 px-4 rounded-lg font-bold text-sm uppercase tracking-wide btn-spyred-hover active:scale-95 transition-all shadow-md"}
          >
            <Ticket size={16} />
            {movie.is_canceled ? 'Cancelled' : (eventEnded ? 'Event Ended' : (isBookingClosed ? 'Booking Closed' : 'Book Now'))}
          </button>

          <button
            onClick={() => onViewMore(movie.id)}
            className={secondaryButtonClass}
          >
            <Info size={16} />
            View More
          </button>
        </div>
      </div>
    </div>
  );
};
